import { useEffect, useState, MouseEvent } from "react";
import { useGetSinglePredictionResultsetsCountResultsetSinglePredictionCountGet, useGetSinglePredictionResultsetsResultsetSinglePredictionGet, useGetSinglePredictionResultsetTagsResultsetSinglePredictionTagsGet } from "../../../api/generated/fastAPI"
import { TableData, TableHeader } from "../../../components/Table/DataTable";
import { GetSinglePredictionResultsetsResultsetSinglePredictionGetParams, Resultset } from "../../../api/models";
import FilterBar, { FilterBarType } from "../../../components/Filtering/FilterBar";
import { useNavigate } from "react-router-dom";
import Dropdown, { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import useDataParams, { GetParamsTypes } from "../../../hooks/useDataParams";
import DeleteModal, { DeleteItemType } from "../../../components/Popups/DeleteModal";
import SearchBar from "../../../components/Filtering/SearchBar";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import UpdatedPagination from "../../../components/Filtering/UpdatedPagination";
import { exportFileExtensions } from "../../../utils/supplementalData";
import { exportData } from "../../../utils/formattingFns";
import useDelete from "../../../hooks/endpoints/useDelete";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";

const predictionHeaders: TableHeader[] = [
    {
        label: 'Name',
        value: 'name',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Compound',
        value: 'smiles',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Tags',
        value: 'tags',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Created By',
        value: 'created_by',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Owned By',
        value: 'owner',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Created',
        value: 'creation_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Updated',
        value: 'updated_date',
        isShown: true,
        alignment: 'center',
    },
]

const defaultParams: GetSinglePredictionResultsetsResultsetSinglePredictionGetParams = {
    name: null,
    created_by: null,
    owner: null,
    start_date: null,
    end_date: null,
    tag_groups: null,
    sort_by: 'creation_date',
    sort_order: 'desc',
    limit: 10,
    offset: 0,
}

const defaultFilters: FilterBarType[] = [
    {
        label: 'Created Date Range',
        param: 'start_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Created Date Range',
        param: 'end_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Tags',
        param: 'tag_groups',
        type: 'tags',
        isDefault: true,
    },
    {
        label: 'Created By',
        param: 'created_by',
        type: 'users',
        isDefault: true,
    },
    {
        label: 'Owner',
        param: 'owner',
        type: 'users',
        isDefault: true,
    },
]

export default function SingleCompoundPredictions({
    setPopupMessage,
}: {
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
}) {
    const getPredTags = useGetSinglePredictionResultsetTagsResultsetSinglePredictionTagsGet();
    
    const {
        params,
        setParams,
        search,
        sort,
        filters,
        setPaginationLimit,
        setPaginationOffset,
    } = useDataParams({
        defaultParams,
        defaultFilters,
        tagsData: getPredTags.data as unknown as { [key: string]: string[] },
    })

    const getPredictionsMutation = useGetSinglePredictionResultsetsResultsetSinglePredictionGet(params as GetParamsTypes);
    const getPredictionsCountMutation = useGetSinglePredictionResultsetsCountResultsetSinglePredictionCountGet({
        name: (params as GetParamsTypes).name!,
        created_by: (params as GetParamsTypes).created_by!,
        owner: (params as GetParamsTypes).owner!,
        start_date: (params as GetParamsTypes).start_date!,
        end_date: (params as GetParamsTypes).end_date!,
        tag_groups: (params as GetParamsTypes).tag_groups!,
    });
    const [ count, setCount ] = useState(0);


    const [ predictions, setPredictions ] = useState<TableData[]>([]);
    const [ allPredictions, setAllPredictions ] = useState<Resultset[]>([]);
    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);
    const [ tableHeaders, setTableHeaders ] = useState<TableHeader[]>(predictionHeaders);
    const [ actionDropdownOptions, setActionDropdownOptions ] = useState<DropdownOption[]>([]);

    const navigate = useNavigate();

    const refetchPredictions = () => {
        getPredictionsMutation.refetch();
        getPredictionsCountMutation.refetch();
    }

    const { deleteResultsets } = useDelete({ setPopupMessage, refetchFn: refetchPredictions });


    useEffect(() => {
        if (getPredictionsMutation.data) {
            const predData = getPredictionsMutation.data as unknown as Resultset[];
            setAllPredictions(predData);

            const items: TableData[] = predData.map(result => ({
                id: result.id!.toString(),
                fields: [
                    {
                        associatedColumn: 'name',
                        value: result.name ? result.name : 'No value',
                        alignment: 'left' as any,
                        type: 'text' as any,
                        action: (e:MouseEvent<HTMLButtonElement>) => navigate(`/resultsets/compound/${result.id!}`),
                    },
                    {
                        associatedColumn: 'smiles',
                        value: result.resultset_metadata!.smiles! as string,
                        alignment: 'center' as any,
                        type: 'structure' as any,
                    },
                    {
                        associatedColumn: 'tags',
                        value: result.resultset_metadata!.tags! as string[],
                        alignment: 'center' as any,
                        type: 'tags' as any,
                    },
                    {
                        associatedColumn: 'created_by',
                        value: result.created_by ? result.created_by : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                    {
                        associatedColumn: 'owner',
                        value: result.owner ? result.owner : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                    {
                        associatedColumn: 'creation_date',
                        value: result.creation_date ? new Date(result.creation_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                    {
                        associatedColumn: 'updated_date',
                        value: result.updated_date ? new Date(result.updated_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                ],
                options: injectOptions(result),
            }))

            setPredictions(items);
        }
        //eslint-disable-next-line
    }, [getPredictionsMutation.data]);

    useEffect(() => {
        if (getPredictionsCountMutation.data) {
            const data = getPredictionsCountMutation.data as unknown as number;
            setCount(data);
        }
    }, [getPredictionsCountMutation.data]);

    const injectOptions = (prediction: Resultset) => {
        const id = prediction.id!;
        const name = prediction.name!;

        return [
            {
                label: 'Export',
                action: (e:MouseEvent<HTMLButtonElement>) => {},
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            try {
                                await exportData(
                                    [{ name, hash: id ? id.toString() : '' }],
                                    ext.file_type,
                                    'resultset',
                                );
    
                                setPopupMessage('You have successfully downloaded the selected resultset.', true);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading this resultset. Please try again.', false);
                            }
                        }
                    }
                ))
            },
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'resultset',
                            id,
                            name,
                        }]}
                        deleteFn={deleteResultsets}
                    />
                )
            },
        ]
    };

    useEffect(() => {
        let options: DropdownOption[] = [];

        if (allPredictions.length > 0 && selectedItems.length > 0) {
            let items: DeleteItemType[] = [];
            let exportItems: { name: string, hash: string }[] = [];
            selectedItems.forEach(id => {
                const matchingItem = allPredictions.find(rs => rs.id === id);
                if (matchingItem) {
                    items.push({
                        type: 'resultset',
                        id,
                        name: matchingItem.name ? matchingItem.name : ''
                    });

                    exportItems.push({ hash: id.toString(), name: matchingItem.name ? matchingItem.name : '' });
                }
            });

            const deleteModalComp: DropdownOption = {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={items}
                        cancelFn={() => setSelectedItems([])}
                        deleteFn={deleteResultsets}
                    />
                ),
            };

            const exportModalComp: DropdownOption = {
                label: 'Export',
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            try {
                                await exportData(
                                    exportItems,
                                    ext.file_type,
                                    'resultset',
                                );
    
                                setPopupMessage('You have successfully downloaded the selected resultsets.', true);
                                setSelectedItems([]);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading the selected resultsets. Please try again.', false);
                            }
                        }
                    }
                ))
            };

            options.push(exportModalComp);
            options.push(deleteModalComp);
        };

        setActionDropdownOptions(options);

        //eslint-disable-next-line
    }, [selectedItems, allPredictions]);


    return (
        <>
            <div className="flex flex-col justify-start items-start gap-2">
                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        <SearchBar
                            searchFn={search}
                            searchKey="name"
                        />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={tableHeaders} 
                            setHeaders={setTableHeaders} 
                            isDisabled={false}
                        />
                        {selectedItems.length > 0 && (
                            <Dropdown 
                                btn={{
                                    label: `Actions (${selectedItems.length} selected)`,
                                    type: 'secondary',
                                    styling: 'solid',
                                    id: 'resultset-multi-action-btn',
                                }}
                                options={actionDropdownOptions}
                            />
                        )}
                    </div>
                </div>
                <FilterBar
                    filters={filters}
                    setParams={setParams}
                    params={params}
                />
            </div>

            <VirtualizedDataTable 
                paramValues={Object.entries(params).filter(entry => ['name', 'start_date', 'end_date'].includes(entry[0]) && entry[1]).map(val => val[1])}
                isLoading={getPredictionsMutation.isLoading} 
                headers={tableHeaders} 
                tableData={predictions}
                selection={{
                    selectedItems,
                    setSelectedItems
                }}
                sorting={{
                    sortableHeaders: ['name', 'created_by', 'owner', 'creation_date', 'updated_date'],
                    sortHeaders: sort,
                    sortByParam: params.sort_by!,
                    sortOrderParam: params.sort_order!,
                }}
                setPopupMessage={setPopupMessage}
                fixedItemHeight={194}
                count={getPredictionsCountMutation.data as unknown as number | undefined}
                dataType="single-predictions"
            />

            <UpdatedPagination 
                count={count}
                limit={params.limit!}
                offset={params.offset!}
                setPaginationLimit={setPaginationLimit}
                setPaginationOffset={setPaginationOffset}
                size="reg"
                isLoading={getPredictionsCountMutation.isLoading}
            />
        </>
    )
}